import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';
import { Dropdown } from '@/components/ui/Dropdown';
import { FlowMark } from '@/components/ui/FlowMark';
import { Tooltip } from '@/components/ui/Tooltip';
import { AIImportDialog } from '@/components/ai-import/AIImportDialog';
import { NotificationsDropdown } from '@/components/notifications/NotificationsDropdown';
import {
  Bell,
  Plus,
  Building2,
  UserCircle,
  CheckSquare,
  MessageSquarePlus,
  Menu,
  Briefcase,
  Sparkles,
  CircleHelp,
  BookOpen,
  LifeBuoy,
  BookA,
} from 'lucide-react';
// pageHelp, never helpCenter: this bar is in every visit's first download, and
// helpCenter carries the whole FAQ.
import { helpForPath } from '@/pages/help/pageHelp';
import { emitShellEvent, useShellEvent } from './shellEvents';

interface HeaderProps {
  sidebarCollapsed: boolean;
}

/** An icon button on the dark chrome. */
const chromeButton =
  'relative flex h-8 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70';

/**
 * The top bar: global, page-agnostic actions — quick-create, feedback and
 * notifications. It is one dark surface with the sidebar; the content panel
 * below is inset in it. The account menu lives once, in the sidebar's footer,
 * and is intentionally not repeated here.
 */
export function Header({ sidebarCollapsed }: HeaderProps) {
  const navigate = useNavigate();
  const toggleMobileMenu = useUIStore((s) => s.toggleMobileMenu);
  const [isAIImportOpen, setIsAIImportOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();

  // The command palette offers this as an action; the dialog lives here.
  useShellEvent('open-ai-import', () => setIsAIImportOpen(true));

  // Resume AI Import after a Microsoft sign-in redirect. The AI Import gate sets
  // this flag before redirecting to Microsoft (popups are unreliable in Safari),
  // and MicrosoftCallback returns the user to the page they left. Header is
  // always mounted in the authenticated layout, so consuming the flag here
  // reopens the dialog no matter which page they land back on. The flag carries
  // a timestamp so an abandoned attempt can't reopen the dialog much later.
  useEffect(() => {
    const raw = localStorage.getItem('flow_reopen_ai_import');
    if (!raw) return;
    localStorage.removeItem('flow_reopen_ai_import');
    if (Date.now() - Number(raw) < 5 * 60 * 1000) {
      setIsAIImportOpen(true);
    }
  }, []);

  const quickAddItems = [
    {
      label: 'AI Import (from files)',
      value: 'ai_import',
      icon: <Sparkles className="h-4 w-4" />,
    },
    {
      label: 'New Deal',
      value: 'deal',
      icon: <Briefcase className="h-4 w-4" />,
    },
    {
      label: 'New Property',
      value: 'property',
      icon: <Building2 className="h-4 w-4" />,
    },
    {
      label: 'New Contact',
      value: 'contact',
      icon: <UserCircle className="h-4 w-4" />,
    },
    {
      label: 'New Task',
      value: 'task',
      icon: <CheckSquare className="h-4 w-4" />,
    },
  ];

  const handleQuickAdd = (type: string) => {
    switch (type) {
      case 'ai_import':
        setIsAIImportOpen(true);
        break;
      case 'deal':
        navigate('/deals/new');
        break;
      case 'property':
        navigate('/properties/new');
        break;
      case 'contact':
        navigate('/contacts/new');
        break;
      case 'task':
        navigate('/tasks/new');
        break;
    }
  };

  const unreadLabel =
    unreadCount > 0
      ? `Notifications, ${unreadCount} unread`
      : 'Notifications';

  // HELP, FROM WHEREVER YOU ARE. The Help Center used to be reachable only from
  // the account menu at the foot of the sidebar, which is where nobody looks
  // when a page has just confused them. The first item is the guide for the
  // page on screen, when one is written.
  const { pathname } = useLocation();
  const pageHelp = helpForPath(pathname);
  const onHelpPage = pathname === '/help' || pathname.startsWith('/help/');
  const helpItems = [
    ...(pageHelp && !onHelpPage
      ? [{
        label: `Help with ${pageHelp.label}`,
        value: 'page',
        icon: <BookOpen className="h-4 w-4" />,
        description: 'The guide for the page you are on',
      }]
      : []),
    {
      label: 'Help Center',
      value: 'center',
      icon: <LifeBuoy className="h-4 w-4" />,
      description: 'Where things are, guides and answers',
    },
    {
      label: 'Glossary',
      value: 'glossary',
      icon: <BookA className="h-4 w-4" />,
      description: 'What the words in Flow mean',
    },
    {
      label: "What's new",
      value: 'whats-new',
      icon: <Sparkles className="h-4 w-4" />,
    },
  ];
  const handleHelp = (value: string) => {
    if (value === 'page' && pageHelp) navigate(pageHelp.href);
    else if (value === 'center') navigate(onHelpPage ? '/help' : `/help?from=${encodeURIComponent(pathname)}`);
    else if (value === 'glossary') navigate('/help/glossary');
    else if (value === 'whats-new') navigate('/whats-new');
  };

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 h-header bg-chrome transition-[left] duration-200 ease-out print:hidden',
        // Desktop: adjust for sidebar
        sidebarCollapsed ? 'lg:left-16' : 'lg:left-60',
        // Mobile: full width
        'left-0'
      )}
    >
      <div className="flex h-full items-center justify-between gap-2 px-3 lg:px-6">
        {/* Mobile: menu + brand */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={toggleMobileMenu}
            className={cn(chromeButton, 'h-9 w-9')}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <FlowMark size={24} title={null} />
          <span className="text-sm font-semibold text-white">Flow</span>
        </div>

        {/* Spacer to keep actions right-aligned */}
        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Quick Add */}
          <Dropdown
            align="right"
            label="Create"
            width={208}
            trigger={
              <button
                type="button"
                aria-label="Create new"
                title="Create new"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-white shadow-button transition-colors hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </button>
            }
            items={quickAddItems}
            onSelect={handleQuickAdd}
          />

          {/* Feedback */}
          <button
            type="button"
            onClick={() => emitShellEvent('open-feedback')}
            className={cn(chromeButton, 'w-8 gap-2 text-[13px] font-medium sm:w-auto sm:px-2.5')}
            aria-label="Send feedback"
          >
            <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Feedback</span>
          </button>

          {/* Help */}
          <Dropdown
            align="right"
            label="Help"
            width={256}
            trigger={
              <button
                type="button"
                aria-label="Help"
                title="Help"
                className={cn(chromeButton, 'w-8 gap-2 text-[13px] font-medium sm:w-auto sm:px-2.5')}
              >
                <CircleHelp className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Help</span>
              </button>
            }
            items={helpItems}
            onSelect={handleHelp}
          />

          {/* Notifications */}
          <Tooltip content="Notifications" side="bottom" disabled={isNotificationsOpen}>
            <button
              ref={notificationsButtonRef}
              type="button"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className={cn(chromeButton, 'w-8', isNotificationsOpen && 'bg-white/10 text-white')}
              aria-label={unreadLabel}
              aria-haspopup="dialog"
              aria-expanded={isNotificationsOpen}
            >
              <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-bold tabular-nums text-white ring-2 ring-chrome">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </Tooltip>
          <NotificationsDropdown
            isOpen={isNotificationsOpen}
            onClose={() => setIsNotificationsOpen(false)}
            triggerRef={notificationsButtonRef}
          />
        </div>
      </div>

      {/* AI Import Dialog */}
      <AIImportDialog
        isOpen={isAIImportOpen}
        onClose={() => setIsAIImportOpen(false)}
      />
    </header>
  );
}
